import TextUtil from "../../../../../lib/html5-software-library/src/ranj/components/text/TextUtil";
import '../../../../assets/skins/TextPositioningAssets'; //Import asset file(s) used by this module.
import FontOffsetParams from "./FontOffsetParams";

export default class FontOffsetCalculator {

    /**
     * Calculate correctional offsets and multipliers for Text objects exported from Adobe Animate.
     * 
     * Make sure fonts are loaded when calling this method. 
     * Call this method before creating skins which contain Text objects. 
     * Typically, call it before showing splash screen.
     * 
     * Required:
     * A movieclip lib.TextPositioningAssets.TextSamplesSkin that contains the following on first and only frame:
     *  - At least 2 textfields with different font sizes per font. More is better.
     *  - First font will be default. Fonts that are not in this movieclip will use the values of the default font. Often, analysing 1 font is enough.
     *  - Using a wide spread of font sizes (e.g.: 30, 60, 90, 150) will give best results. 
     *    We sample pixels of rendered text. So large font sizes work better because it reduces errors due to anti-aliasing.
     *    But if you use only large font sizes, the results may be off a bit for smaller fonts.
     *  - All textfields should be aligned with their leftmost pixel exactly at x 100 and their topmost pixel exactly at y 100. 
     *    More exact positioning will result in better fixed positions at runtime.
     *  - Textfields should not be scaled, rotated or skewed. Textfields that are transformed in any other way than positioning will be ignored.
     *  - Textfields should have alpha 1. Other values will be ignored.
     *  - All textfields for same font should start with the same character. A good practice is to use a capital 'F' or any other character that has a clear top-left point.
     * 
     * @returns object with font names as key and FontPositionFixParams as value
     */
    static calculateFontOffsetParameters() {

        const offsetsPerFont = readFontOffsets();

        //Now we have for each font the offsets by font size
        //Calculate avg offset and multiplier for x and y
        //These can be seen as the y-intersect and slope of a line equation
        const paramsPerFont = {};
        for (const [fontName, paramSets] of Object.entries(offsetsPerFont)) {
            let yOffsetAvg = 0, yMultiplierAvg = 0;
            let xOffsetAvg = 0, xMultiplierAvg = 0;
            const n = paramSets.length;
            if (n > 1) {
                //Calc offsets and multipliers using all sequentials pairs of sizes, and average the results.
                //  Using sequential pairs is no guarantee that the best values are obtained, but it seems to give good results. 
                //  It seems the incorrectness in positioning is not completely linear with font size, else we could just compare any 2 sizes.
                //  Averaging also rules out errors due to inexact positioning in the fla file.
                paramSets.sort((p1, p2) => p1.fontSize - p2.fontSize);
                for (let i = 0; i < n - 1; i++) {
                    const { fontSize: size1, dx: dx1, dy: dy1 } = paramSets[i];
                    const { fontSize: size2, dx: dx2, dy: dy2 } = paramSets[i + 1];
                    const { offset: offsetY, multiplier: multiplierY } = calcOffsetAndMultiplier(dy1, dy2, size1, size2);
                    yOffsetAvg += offsetY / (n - 1);
                    yMultiplierAvg += multiplierY / (n - 1);
                    const { offset: offsetX, multiplier: multiplierX } = calcOffsetAndMultiplier(dx1, dx2, size1, size2);
                    xOffsetAvg += offsetX / (n - 1);
                    xMultiplierAvg += multiplierX / (n - 1);
                }
            }
            paramsPerFont[fontName] = new FontOffsetParams(xOffsetAvg, xMultiplierAvg, yOffsetAvg, yMultiplierAvg);
        }

        return paramsPerFont;
    }
}

/**
 * Reads offsets of all fonts from an asset exported by Adobe Animate.
 * See requirements in method comment of TextOffsetCalculator.calculateFontParameters
 * @returns 
 */
const readFontOffsets = () => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    const skin = new lib.TextPositioningAssets.TextSamplesSkin();
    //Collect all Text objects that have a valid transformation (translation only)
    const texts = skin.children.filter(obj => {
        if (obj instanceof createjs.Text) {
            const matrix = obj.getMatrix();
            if (matrix.a != 1 || matrix.b != 0 || matrix.c != 0 || matrix.d != 1) {
                console.error(`Text obj with font '${obj.font}' is rotated, scaled or skewed, and will be ignored.`);
                return false;
            }
            if (obj.alpha != 1) {
                console.error(`Text obj with font '${obj.font}' has alpha < 1, and will be ignored.`);
                return false;
            }
            return true;
        };
        return false;
    }).reverse();

    skin.children.forEach(obj => { obj.visible = false; });

    //refX and refY are the coordinate where the Text objects are placed with their top-left-most pixel
    const refX = 100, refY = 100;
    const offsetsPerFont = {};

    const firstLettersByFontName = {};
    texts.forEach(textObj => {
        const fontName = TextUtil.getFontName(textObj);
        const firstChar = textObj.text.charAt(0);
        if (firstLettersByFontName[fontName] == null) {
            firstLettersByFontName[fontName] = firstChar;
        } else if (firstLettersByFontName[fontName] != firstChar) {
            console.error(`Text obj with font '${textObj.font}' has mismatching first char '${firstChar}'. Should be '${firstLettersByFontName[fontName]}'. Object will be ignored.`);
            return;
        }
        //Render the current text to the canvas
        textObj.visible = true;
        context.clearRect(0, 0, canvas.width, canvas.height);
        skin.draw(context);
        textObj.visible = false;
        //Find top and left of rendered text
        const scanSize = 150;
        const data = context.getImageData(0, 0, scanSize, scanSize).data;
        let minx = Infinity, miny = Infinity;
        for (let y = 0; y < scanSize; y++) {
            for (let x = 0; x < scanSize; x++) {
                const alpha = data[(y * scanSize + x) * 4 + 3];
                //The threshold corrects for antialiasing. Its value is an experiental value.
                if (alpha > ANTIALIAS_THRESHOLD) {
                    minx = Math.min(x, minx);
                    miny = Math.min(y, miny);
                }
            }
        }
        const fontSize = TextUtil.getFontSize(textObj);
        const dx = refX - minx, dy = refY - miny;
        if (offsetsPerFont[fontName] == null) {
            offsetsPerFont[fontName] = [];
        }
        offsetsPerFont[fontName].push({ fontSize, dx, dy });
    });
    return offsetsPerFont;
}

const calcOffsetAndMultiplier = (d1, d2, size1, size2) => {
    const multiplier = (d2 - d1) / (size2 - size1);//slope
    const offset = d1 - (multiplier * size1);//y-intercept
    return { offset, multiplier };
}

const ANTIALIAS_THRESHOLD = 128;